import { useCallback, useEffect, useMemo } from 'react';

import { EntityList, EntityListParams, EntityRepository, EntityWithDetails } from '~/repositories/EntityRepository';
import { EntityEntity, EntityServiceGetEntitiesParams } from '~/typings/API';

import { useDebounce } from './useDebounce';
import { createApiHook } from './utils/createApiHook';

export const useEntities = createApiHook<EntityList, EntityListParams>(EntityRepository.getAll, {
  triggerAutomatically: true,
  triggerOnSessionStoreSubscribe: true,
  includeQueryParams: true,
});

export const useEntity = createApiHook<EntityWithDetails, GetRequestType>(EntityRepository.get);

export const useEntityDropdown = (entityId?: string, initialQueryParams?: EntityListParams) => {
  const { response: entities, setQueryParams: searchEntities, isInitialLoad } = useEntities({ initialQueryParams });
  const { response: activeEntity, createRequest: fetchEntityActive } = useEntity();

  const getOption = useCallback(
    (entity: EntityEntity | undefined) =>
      entity && {
        label: entity.name,
        value: entity.id,
      },
    []
  );

  useEffect(() => {
    if (entityId) {
      fetchEntityActive({ id: entityId });
    }
  }, [entityId, fetchEntityActive]);

  const options = useMemo(
    () =>
      (entities?.entities ?? [])
        .filter(Boolean)
        .map(getOption)
        .filter((option) => option !== undefined) ?? [],
    [entities, getOption]
  );

  const hiddenOptions = useMemo(
    () =>
      options.some((option) => option.value === activeEntity?.id)
        ? []
        : ([activeEntity]
            .filter(Boolean)
            .map(getOption)
            .filter((option) => option !== undefined) ?? []),
    [activeEntity, getOption, options]
  );

  const debouncedSearchEntities = useDebounce(
    (newParams: EntityServiceGetEntitiesParams | undefined, overwrite?: boolean) =>
      searchEntities(newParams, overwrite),
    300
  );

  return {
    entities,
    options,
    hiddenOptions,
    searchEntities: debouncedSearchEntities,
    isLoading: isInitialLoad,
    activeEntity: activeEntity as EntityEntity | undefined,
  };
};
